import type React from "react";
import { Link } from "react-router";
import { Helmet } from "react-helmet-async";
import type { BlogPost } from "./posts";
import { getRelatedPosts } from "./posts";

interface BlogLayoutProps {
  post: BlogPost;
  children: React.ReactNode;
}

const BlogLayout: React.FC<BlogLayoutProps> = ({ post, children }) => {
  const relatedPosts = getRelatedPosts(post.slug);

  return (
    <>
      <Helmet>
        <title>{post.title} — MycenAI</title>
        <meta name="description" content={post.metaDescription} />
        <link
          rel="canonical"
          href={`https://mycenai.org/blog/${post.slug}/`}
        />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content={`https://mycenai.org/blog/${post.slug}/`}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.metaDescription,
            "datePublished": post.isoDate,
            "url": `https://mycenai.org/blog/${post.slug}/`,
            "publisher": {
              "@type": "Organization",
              "name": "MycenAI",
              "url": "https://mycenai.org"
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://mycenai.org/blog/${post.slug}/`
            }
          })}
        </script>
      </Helmet>

      <div className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6">
        <Link
          to="/blog"
          className="mb-8 inline-block text-gray-500 text-sm transition hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          ← Back to Blog
        </Link>

        <header className="mb-10">
          <h1 className="mb-4 font-bold text-3xl text-gray-900 leading-tight sm:text-4xl dark:text-white">
            {post.title}
          </h1>
          <div className="flex items-center gap-3 text-gray-500 text-sm dark:text-gray-400">
            <time>{post.date}</time>
            <span>·</span>
            <span>{post.readingTime}</span>
          </div>
        </header>

        <article className="prose prose-gray dark:prose-invert max-w-none prose-blockquote:border-gray-300 prose-headings:font-semibold prose-a:text-violet-600 prose-a:no-underline hover:prose-a:underline dark:prose-blockquote:border-gray-600 dark:prose-a:text-violet-400">
          {children}
        </article>

        {relatedPosts.length > 0 && (
          <section className="mt-16 border-gray-200 border-t pt-10 dark:border-gray-700">
            <h2 className="mb-6 font-semibold text-gray-900 text-xl dark:text-white">
              More from MycenAI
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {relatedPosts.map((related) => (
                <Link
                  key={related.slug}
                  to={`/blog/${related.slug}`}
                  className="group rounded-lg border border-gray-200 p-5 transition hover:border-violet-300 hover:shadow-sm dark:border-gray-700 dark:hover:border-violet-600"
                >
                  <h3 className="mb-2 font-semibold text-gray-900 text-sm leading-snug transition group-hover:text-violet-600 dark:text-white dark:group-hover:text-violet-400">
                    {related.title}
                  </h3>
                  <p className="text-gray-500 text-xs dark:text-gray-400">
                    {related.date}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default BlogLayout;
